import * as B from '@blueprintjs/core';
import {ErrorBoundary} from '@sentry/react';
import classNames from 'classnames';
import React from 'react';

import type {ApiImageryBudget} from 'app/modules/Remote/api';
import {
  ApiBulkOrderVerification,
  ApiBulkOrderVerificationError,
  ApiBulkOrderVerificationSuccess,
  ApiFeature,
} from 'app/modules/Remote/Feature/types';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiImageryContract, ApiProject} from 'app/modules/Remote/Project/types';
import {
  useCurrentImageryBudget,
  useCurrentImageryContract,
} from 'app/providers/ImageryContractProvider';
import * as CONSTANTS from 'app/utils/constants';
import * as costUtils from 'app/utils/costUtils';
import {useUserInfo} from 'app/utils/featureFlags';
import {formatFeatureName} from 'app/utils/featureUtils';
import {StatusMaybe, useTimestampMs} from 'app/utils/hookUtils';
import {BULK_ORDER_LAYER_KEYS, PER_FEATURE_BULK_ORDER_SOURCE_IDS} from 'app/utils/layers';
import {parseLayerKey} from 'app/utils/layerUtils';
import {mayOrderImagery} from 'app/utils/userUtils';

import {useBulkSensingTime, usePlaceBulkOrder, useVerifyOrderImageryBulk} from './hooks';
import {
  ContractDetailsDescription,
  NteScope,
  getPackageOrderText,
  purchaseInfoStatus,
} from './orderImageryUtils';
import cs from './styles.module.scss';
import {usePushNotification} from '../Notification';

export const BulkOrderImageryModal: React.FunctionComponent<
  React.PropsWithChildren<{
    features: ApiFeature[];
    isOpen: boolean;
    onClose: () => void;
    onSubmit: () => void;
    layerKey: string;
    project: ApiProject;
    featureCollection: ApiFeatureCollection;
  }>
> = ({features, layerKey, isOpen = true, onClose, onSubmit, project, featureCollection}) => {
  const [getCurrentImageryContract] = useCurrentImageryContract();
  const [contractAfterMs] = useTimestampMs();
  const {sourceId} = parseLayerKey(layerKey);
  const pushNotification = usePushNotification();
  const [_, profile] = useUserInfo();
  const isPerFeatureSource = PER_FEATURE_BULK_ORDER_SOURCE_IDS.has(sourceId);
  const [sensingTimeMode, setSensingTimeMode] = React.useState<'all' | 'latest'>('latest');

  const imageryContractStatusMaybe = getCurrentImageryContract(project.id, {
    afterMs: contractAfterMs,
    allowStaleMs: contractAfterMs - 60 * 1000,
  });
  const imageryContract =
    imageryContractStatusMaybe.value &&
    imageryContractStatusMaybe.value.contractType === CONSTANTS.CONTRACT_TYPE_DOLLAR_UNIT
      ? imageryContractStatusMaybe.value
      : null;
  const imageryBudgetQuery = useCurrentImageryBudget(project.organizationId);
  const imageryBudget = imageryBudgetQuery.data ?? null;
  const purchaseStatus = purchaseInfoStatus(imageryContractStatusMaybe, imageryBudgetQuery);

  const sensingTime = useBulkSensingTime(
    featureCollection.id,
    project.id,
    features.map((feature) => feature.id as number),
    sourceId
  );

  const {
    data: verifyOrderResponse,
    isLoading: isLoadingVerifyingOrder,
    isPending: isPendingVerifyOrder,
    isError: isErrorVerifyingOrder,
    error: verifyOrderError,
  } = useVerifyOrderImageryBulk({
    featureCollection,
    project,
    features: features.map((feature) => feature.id as number),
    sensingTime: sensingTime ?? null,
    sourceId,
    // for per-feature sources, we can pass 'latest' or 'all'; for package sources, we always pass 'explicit'
    sensingTimeMode: isPerFeatureSource ? sensingTimeMode : 'explicit',
    // sensingTime is null in two distinct cases: for per-feature sources (intentional — backend
    // selects scenes per feature) and for package sources while the sensing time is still loading.
    // isPerFeatureSource disambiguates: per-feature sources fire immediately with null sensingTime,
    // package sources wait until sensingTime resolves to a string.
    enabled: !!sensingTime || isPerFeatureSource,
  });

  const scenes = verifyOrderResponse || [];
  const validScenes = scenes.filter((r) => 'costInCents' in r);
  const totalCostInCents = validScenes.reduce((accum, scene) => accum + scene.costInCents, 0);
  const errorScenes = scenes.filter((r) => 'error' in r);
  const alreadyPurchasedScenes = errorScenes.filter((s) => s.error.includes('already purchased'));
  const invalidScenes = errorScenes.filter((s) => s.error.includes('invalid scene'));

  const {mutateAsync: placeBulkOrder, isPending: isPlacingBulkOrder} = usePlaceBulkOrder({
    featureCollection,
    project,
    sensingTime: sensingTime ?? null,
    sourceId,
    sensingTimeMode: isPerFeatureSource ? sensingTimeMode : 'explicit',
  });

  const isVerifyingOrder = isPendingVerifyOrder || isLoadingVerifyingOrder;
  const canPurchase =
    imageryContract &&
    purchaseStatus === 'some' &&
    (validScenes?.length ?? 0) > 0 &&
    totalCostInCents > 0 &&
    !isVerifyingOrder &&
    !isErrorVerifyingOrder &&
    !isPlacingBulkOrder;

  return (
    <ErrorBoundary
      fallback={
        <B.NonIdealState
          icon="error"
          iconMuted={false}
          title="Error"
          description={'Unable to load bulk order imagery modal.'}
        />
      }
    >
      <B.MultistepDialog
        className={classNames(cs.content, cs.bulkOrderModalContent)}
        isOpen={isOpen}
        onClose={onClose}
        title="Bulk Order Confirmation"
        finalButtonProps={{
          text: `Order for ${costUtils.formatCost(totalCostInCents)}`,
          disabled: !mayOrderImagery(profile.get('role')) || !canPurchase || isPlacingBulkOrder,
          onClick: async () => {
            const featureIds = validScenes?.map((scene) => scene.featureId) || [];
            await placeBulkOrder({
              featureIds,
              expectedCostInCents: totalCostInCents,
              pushNotification,
            });
            if (onSubmit) {
              onSubmit();
            }
          },
        }}
        backButtonProps={{
          text: 'Back',
          disabled: isPlacingBulkOrder,
        }}
      >
        <B.DialogStep
          id="calculate-cost"
          title="Calculate Cost"
          nextButtonProps={{
            disabled: !canPurchase || isVerifyingOrder,
            text: 'Review Order',
          }}
          panel={
            <CalculateCostPanel
              features={features}
              layerKey={layerKey}
              validScenes={validScenes}
              alreadyPurchasedScenes={alreadyPurchasedScenes}
              purchaseStatus={purchaseStatus}
              nteScope={imageryBudget ? 'organization' : 'contract'}
              verifyOrderError={verifyOrderError}
              isVerifyingOrder={isVerifyingOrder}
              isPerFeatureSource={isPerFeatureSource}
              sensingTimeMode={sensingTimeMode}
              onSensingTimeModeChange={setSensingTimeMode}
            />
          }
        />
        <B.DialogStep
          id="review-order"
          title="Review Order"
          nextButtonProps={{
            text: 'Confirm Order',
            disabled: !canPurchase,
          }}
          panel={
            <ReviewOrderPanel
              features={features}
              validScenes={validScenes}
              alreadyPurchasedScenes={alreadyPurchasedScenes}
              invalidScenes={invalidScenes}
              layerKey={layerKey}
              imageryContract={imageryContract!} // Guaranteed to be non-null if we made it to this step because canPurchase is true
              imageryBudget={imageryBudget}
              isPlacingBulkOrder={isPlacingBulkOrder}
              sensingTimeMode={isPerFeatureSource ? sensingTimeMode : 'explicit'}
            />
          }
        />
      </B.MultistepDialog>
    </ErrorBoundary>
  );
};

const CalculateCostPanel = ({
  features,
  layerKey,
  validScenes,
  alreadyPurchasedScenes,
  purchaseStatus,
  nteScope,
  verifyOrderError,
  isVerifyingOrder,
  isPerFeatureSource,
  sensingTimeMode,
  onSensingTimeModeChange,
}: {
  features: ApiFeature[];
  layerKey: string;
  validScenes: ApiBulkOrderVerificationSuccess[];
  alreadyPurchasedScenes: ApiBulkOrderVerificationError[];
  purchaseStatus: StatusMaybe<unknown>['status'];
  nteScope: NteScope;
  verifyOrderError: Error | null;
  isVerifyingOrder: boolean;
  isPerFeatureSource: boolean;
  sensingTimeMode: 'all' | 'latest';
  onSensingTimeModeChange: (mode: 'all' | 'latest') => void;
}) => {
  const totalCostInCents = validScenes.reduce((accum, scene) => accum + scene.costInCents, 0);
  // Its wording names whose funds ran out, so it waits for the budget to say which.
  const isOverCap =
    !!verifyOrderError && 'status' in verifyOrderError && verifyOrderError.status === 422;

  return (
    <div className={classNames(cs.content, cs.bulkOrderModalContent, cs.columnPanel)}>
      {isPerFeatureSource && (
        <B.RadioGroup
          label="Scenes to order per property"
          selectedValue={sensingTimeMode}
          onChange={(e) => onSensingTimeModeChange(e.currentTarget.value as 'all' | 'latest')}
        >
          {/**no need for an "explicit" button as that's simply the one set if we're not in a per feature source */}
          <B.Radio label="Latest scene only" value="latest" />
          <B.Radio label="All available scenes" value="all" />
        </B.RadioGroup>
      )}
      {purchaseStatus === 'error' ? (
        <B.NonIdealState
          icon="error"
          iconMuted={false}
          title="Error"
          description={'Unable to load purchase information for this project.'}
        />
      ) : isOverCap && purchaseStatus === 'some' ? (
        <B.Callout intent={B.Intent.WARNING} title="Unable to order bulk selection">
          <p>
            {`You cannot place this order because there are not enough funds remaining for ${
              nteScope === 'organization' ? 'your organization' : 'this portfolio'
            }.`}
          </p>
          <p>Please have an account administrator contact us for options.</p>
        </B.Callout>
      ) : verifyOrderError && !isOverCap ? (
        <B.Callout intent={B.Intent.DANGER} title="Error calculating cost" icon={'error'}>
          <p>
            An error occurred while calculating the cost of your bulk order. Please try again or
            contact support if the issue persists.
          </p>
        </B.Callout>
      ) : isVerifyingOrder || purchaseStatus === 'unknown' ? (
        <>
          <p>Calculating cost of bulk order for {features.length} properties and locations...</p>

          <div className={cs.loadingSpinnerContainer}>
            <B.Spinner className={cs.loadingSpinner} />
          </div>
        </>
      ) : (
        <div className={cs.panelContent}>
          {getPackageOrderText(layerKey, true, sensingTimeMode)}
          {alreadyPurchasedScenes.length === features.length || totalCostInCents === 0 ? (
            <B.Callout intent={B.Intent.DANGER} title="Unable to place bulk order" icon={'error'}>
              This data has already been ordered for all {features.length} properties. Please adjust
              your selection.
            </B.Callout>
          ) : (
            <>
              <B.Callout intent={B.Intent.WARNING} icon={null}>
                Your total comes to <strong>{costUtils.formatCost(totalCostInCents)}</strong>. Your
                selection includes <strong>{validScenes.length} properties and locations</strong>{' '}
                where this data has not already been purchased. You will only be billed for the area
                within each boundary, with vendor minimums applied.
              </B.Callout>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ReviewOrderPanel = ({
  features,
  validScenes,
  invalidScenes,
  alreadyPurchasedScenes,
  layerKey,
  imageryContract,
  imageryBudget,
  isPlacingBulkOrder,
  sensingTimeMode,
}: {
  features: ApiFeature[];
  validScenes: ApiBulkOrderVerificationSuccess[];
  invalidScenes: ApiBulkOrderVerificationError[];
  alreadyPurchasedScenes: ApiBulkOrderVerificationError[];
  layerKey: string;
  imageryContract: ApiImageryContract;
  imageryBudget: ApiImageryBudget | null;
  isPlacingBulkOrder: boolean;
  sensingTimeMode: 'all' | 'latest' | 'explicit';
}) => {
  const [_, profile] = useUserInfo();

  const totalCostInCents = validScenes.reduce((accum, scene) => accum + scene.costInCents, 0);

  const sortedValidScenes = validScenes.sort((a, b) => b.costInCents - a.costInCents);
  const allSortedScenes: ApiBulkOrderVerification[] = [
    ...sortedValidScenes,
    ...alreadyPurchasedScenes,
    ...invalidScenes,
  ];

  return (
    <div className={classNames(cs.content, cs.bulkOrderModalContent)}>
      {isPlacingBulkOrder ? (
        <div className={cs.loadingSpinnerContainer}>
          <p>Placing order...</p>
          <p>This may take a few minutes. Please don't refresh your page.</p>
          <B.Spinner className={cs.loadingSpinner} />
        </div>
      ) : (
        <div className={cs.panelContent}>
          <p>
            The table below shows the cost for the {allSortedScenes.length}{' '}
            {allSortedScenes.length === 1 ? 'property or location ' : 'properties and locations '}
            in your order.
          </p>
          <div className={cs.tableContainer}>
            <table>
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Cost</th>
                  {sensingTimeMode === 'all' && <th style={{width: '8rem'}}>Scenes</th>}
                </tr>
              </thead>
              <tbody>
                {allSortedScenes.map((scene, index) => {
                  const feature = features.find((f) => f.id === scene.featureId);
                  const isSuccess = 'costInCents' in scene;
                  let costString = '';
                  if (isSuccess) {
                    costString = costUtils.formatCost(scene.costInCents);
                  } else if (scene.error.includes('already purchased')) {
                    costString = '$0 (Already purchased)';
                  } else if (scene.error.includes('invalid scene')) {
                    costString = '$0 (Data not available)';
                  }
                  return (
                    <tr key={index}>
                      <td>{formatFeatureName(feature!)}</td>
                      <td>{costString}</td>
                      {sensingTimeMode === 'all' && <td>{isSuccess ? scene.sceneCount : 0}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ContractDetailsDescription
            isDisabledSource={!BULK_ORDER_LAYER_KEYS.has(layerKey)}
            sceneCoverageFailed={false} // This check happens in the api and will come back in the error scenes response
            canPurchase={true}
            layerKey={layerKey}
            orderGeometryAcres={0} // Unused so pass 0 for both acre props
            billableAcres={0}
            imageryContract={imageryContract!}
            imageryBudget={imageryBudget}
            role={profile.get('role')}
            orderCents={totalCostInCents}
            acresMessage={''}
            supportsCopyLink={false}
          />
        </div>
      )}
    </div>
  );
};
